import { createHash, randomUUID } from "node:crypto";
import { gzipSync } from "node:zlib";

import { buildOrgxApiUrl, resolveOrgxAuth } from "./auth.js";
import type { SessionReconciliationReport } from "./work-graph.js";

export interface PublishWorkGraphReportOptions {
  artifactUrl?: string;
  attachArtifact?: boolean;
  entityId?: string;
  entityType?: "project" | "initiative" | "milestone" | "task" | "decision";
  initiativeId?: string;
  publicShare?: boolean;
  signal?: AbortSignal;
  workspaceId?: string;
}

export interface PublishWorkGraphReportResult {
  ok: boolean;
  status: number;
  url: string;
  data: unknown;
}

export interface PublishWorkGraphEventsOptions {
  signal?: AbortSignal;
}

export const WORK_GRAPH_REPORT_GZIP_THRESHOLD_BYTES = 4_000_000;
export const WORK_GRAPH_REPORT_CHUNK_UPLOAD_THRESHOLD_BYTES = 500_000;
export const WORK_GRAPH_REPORT_CHUNK_CHARS = 30_000;
export const WORK_GRAPH_REPORT_CHUNK_UPLOAD_TIMEOUT_MS = 300_000;

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function trimString(value: unknown, max: number): unknown {
  return typeof value === "string" ? value.slice(0, max) : value;
}

function compactNode(value: unknown, summaryMax = 240): unknown {
  const record = asRecord(value);
  return {
    ...record,
    summary: trimString(record.summary, summaryMax),
    evidence_refs: asArray(record.evidence_refs).slice(0, 6),
    linked_node_ids: asArray(record.linked_node_ids).slice(0, 6),
    metadata: undefined,
  };
}

function compactWorkGraphReportForPublish(report: SessionReconciliationReport): SessionReconciliationReport {
  const compacted = JSON.parse(JSON.stringify(report)) as Record<string, unknown>;
  compacted.events = [];

  compacted.findings = asArray(compacted.findings).map((finding) => {
    const record = asRecord(finding);
    const metadata = asRecord(record.metadata);
    return {
      type: record.type,
      title: record.title,
      summary: trimString(record.summary, 320),
      source_client: record.source_client,
      source_id: record.source_id,
      evidence_ref: record.evidence_ref,
      confidence: record.confidence,
      metadata: {
        source_label: metadata.source_label,
        occurred_at: metadata.occurred_at,
        privacy_state: metadata.privacy_state,
      },
    };
  });

  compacted.trails = asArray(compacted.trails).slice(0, 60).map((trail) => {
    const record = asRecord(trail);
    return {
      ...record,
      summary: trimString(record.summary, 500),
      events: asArray(record.events).slice(0, 4).map((event) => {
        const eventRecord = asRecord(event);
        return {
          ...eventRecord,
          redacted_verbatim: trimString(eventRecord.redacted_verbatim, 220),
        };
      }),
      edges: asArray(record.edges).slice(0, 4),
      evidence_refs: asArray(record.evidence_refs).slice(0, 12),
      blocker_ids: asArray(record.blocker_ids).slice(0, 12),
      recommendation_ids: asArray(record.recommendation_ids).slice(0, 12),
    };
  });

  const spine = asRecord(compacted.attribution_spine);
  for (const key of [
    "source_events",
    "actions",
    "decisions",
    "artifacts",
    "people",
    "agents",
    "tools",
    "businesses",
    "product_surfaces",
    "goals",
    "sources",
    "initiative_candidates",
    "evidence_refs",
  ]) {
    const limit = key === "source_events" || key === "evidence_refs" ? 48 : 16;
    spine[key] = asArray(spine[key]).slice(0, limit).map((node) => compactNode(node));
  }
  spine.dedupe_keys = asArray(spine.dedupe_keys).slice(0, 80);
  compacted.attribution_spine = spine;

  const investigation = asRecord(compacted.investigation);
  delete investigation.raw_events;
  investigation.work_loops = asArray(investigation.work_loops).slice(0, 120).map((loop) => {
    const record = asRecord(loop);
    const path = asRecord(record.path);
    return {
      ...record,
      cites: asArray(record.cites).slice(0, 12),
      path: {
        ...path,
        event_ids: asArray(path.event_ids).slice(0, 12),
        actions_taken: asArray(path.actions_taken).slice(0, 8),
        sessions_spanned: asArray(path.sessions_spanned).slice(0, 8),
        tools_used: asArray(path.tools_used).slice(0, 8),
      },
    };
  });
  investigation.loop_families = asArray(investigation.loop_families).slice(0, 24).map((family) => {
    const record = asRecord(family);
    const repair = asRecord(record.orgx_repair);
    const args = asRecord(repair.expected_tool_call_args);
    return {
      ...record,
      loop_ids: asArray(record.loop_ids).slice(0, 24),
      orgx_repair: {
        ...repair,
        expected_tool_call_args: {
          ...args,
          evidence_event_ids: asArray(args.evidence_event_ids).slice(0, 12),
        },
      },
      alternative_explanations_considered: asArray(record.alternative_explanations_considered).slice(0, 2),
      alternative_rejected_because: asArray(record.alternative_rejected_because).slice(0, 2),
    };
  });
  const usage = asRecord(investigation.usage_catalogue);
  investigation.usage_catalogue = {
    ...usage,
    mcp_tools: asArray(usage.mcp_tools).slice(0, 80).map((tool) => ({
      ...asRecord(tool),
      event_refs: asArray(asRecord(tool).event_refs).slice(0, 8),
    })),
    shell_commands: asArray(usage.shell_commands).slice(0, 40).map((command) => ({
      ...asRecord(command),
      event_refs: asArray(asRecord(command).event_refs).slice(0, 8),
    })),
    recipes_or_skills: asArray(usage.recipes_or_skills).slice(0, 24),
    agents_observed: asArray(usage.agents_observed).slice(0, 12),
    repo_paths_touched: asArray(usage.repo_paths_touched).slice(0, 24),
    external_sources_called: asArray(usage.external_sources_called).slice(0, 24),
  };
  investigation.critic_log = asArray(investigation.critic_log).slice(0, 60);
  investigation.verification_log = asArray(investigation.verification_log).slice(0, 120);
  compacted.investigation = investigation;

  return compacted as unknown as SessionReconciliationReport;
}

export function prepareWorkGraphReportForPublish(
  report: SessionReconciliationReport,
): SessionReconciliationReport {
  return compactWorkGraphReportForPublish(report);
}

function hashText(value: string): string {
  return createHash("sha256").update(value).digest("hex");
}

function buildWorkGraphReportPostPayload(
  report: SessionReconciliationReport,
  options: PublishWorkGraphReportOptions = {},
): Record<string, unknown> {
  return {
    report,
    ...(options.workspaceId ? { workspace_id: options.workspaceId } : {}),
    ...(options.initiativeId ? { initiative_id: options.initiativeId } : {}),
    ...(options.entityType ? { entity_type: options.entityType } : {}),
    ...(options.entityId ? { entity_id: options.entityId } : {}),
    ...(options.artifactUrl ? { artifact_url: options.artifactUrl } : {}),
    attach_artifact: Boolean(options.attachArtifact),
    public_share: Boolean(options.publicShare),
  };
}

export function createWorkGraphReportUploadChunks(
  payload: Record<string, unknown>,
  options: { chunkChars?: number; uploadId?: string } = {},
): {
  uploadId: string;
  reportSha256: string;
  totalBytes: number;
  json: string;
  chunks: Array<{ chunkIndex: number; chunkText: string; chunkSha256: string }>;
} {
  const chunkChars = options.chunkChars ?? WORK_GRAPH_REPORT_CHUNK_CHARS;
  const json = JSON.stringify(payload);
  const chunks: Array<{ chunkIndex: number; chunkText: string; chunkSha256: string }> = [];
  for (let offset = 0; offset < json.length; offset += chunkChars) {
    const chunkText = json.slice(offset, offset + chunkChars);
    chunks.push({
      chunkIndex: chunks.length,
      chunkText,
      chunkSha256: hashText(chunkText),
    });
  }

  return {
    uploadId: options.uploadId ?? `wgrup_${randomUUID()}`,
    reportSha256: hashText(json),
    totalBytes: Buffer.byteLength(json),
    json,
    chunks,
  };
}

export function encodeWorkGraphReportPostBody(
  payload: Record<string, unknown>,
  thresholdBytes = WORK_GRAPH_REPORT_GZIP_THRESHOLD_BYTES,
): { body: string | Buffer; headers: Record<string, string>; byteLength: number; compressed: boolean } {
  const json = JSON.stringify(payload);
  const byteLength = Buffer.byteLength(json);
  if (byteLength < thresholdBytes) {
    return {
      body: json,
      headers: { "Content-Type": "application/json" },
      byteLength,
      compressed: false,
    };
  }

  return {
    body: gzipSync(json),
    headers: {
      "Content-Encoding": "gzip",
      "Content-Type": "application/json",
    },
    byteLength,
    compressed: true,
  };
}

async function parseResponse(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

async function postWorkGraphReportJson({
  auth,
  body,
  signal,
  url,
}: {
  auth: NonNullable<Awaited<ReturnType<typeof resolveOrgxAuth>>>;
  body: Record<string, unknown>;
  signal?: AbortSignal;
  url: string;
}): Promise<Response> {
  const requestInit: RequestInit = {
    method: "POST",
    headers: {
      Authorization: `Bearer ${auth.apiKey}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  };
  if (signal) requestInit.signal = signal;
  return fetch(url, requestInit);
}

async function publishWorkGraphReportInChunks({
  auth,
  payload,
  signal,
  url,
}: {
  auth: NonNullable<Awaited<ReturnType<typeof resolveOrgxAuth>>>;
  payload: Record<string, unknown>;
  signal?: AbortSignal;
  url: string;
}): Promise<PublishWorkGraphReportResult> {
  const upload = createWorkGraphReportUploadChunks(payload);
  const uploadSignal = signal ?? AbortSignal.timeout(WORK_GRAPH_REPORT_CHUNK_UPLOAD_TIMEOUT_MS);

  const startResponse = await postWorkGraphReportJson({
    auth,
    body: {
      action: "start",
      upload_id: upload.uploadId,
      chunk_count: upload.chunks.length,
      total_bytes: upload.totalBytes,
      report_sha256: upload.reportSha256,
    },
    signal: uploadSignal,
    url,
  });
  if (!startResponse.ok) {
    return {
      ok: false,
      status: startResponse.status,
      url,
      data: await parseResponse(startResponse),
    };
  }

  for (const chunk of upload.chunks) {
    const chunkResponse = await postWorkGraphReportJson({
      auth,
      body: {
        action: "chunk",
        upload_id: upload.uploadId,
        chunk_index: chunk.chunkIndex,
        chunk_text: chunk.chunkText,
        chunk_sha256: chunk.chunkSha256,
      },
      signal: uploadSignal,
      url,
    });
    if (!chunkResponse.ok) {
      return {
        ok: false,
        status: chunkResponse.status,
        url,
        data: await parseResponse(chunkResponse),
      };
    }
  }

  const completeResponse = await postWorkGraphReportJson({
    auth,
    body: {
      action: "complete",
      upload_id: upload.uploadId,
    },
    signal: uploadSignal,
    url,
  });

  return {
    ok: completeResponse.ok,
    status: completeResponse.status,
    url,
    data: await parseResponse(completeResponse),
  };
}

export async function publishWorkGraphReport(
  report: SessionReconciliationReport,
  options: PublishWorkGraphReportOptions = {},
): Promise<PublishWorkGraphReportResult> {
  const auth = await resolveOrgxAuth();
  if (!auth) {
    throw new Error("OrgX auth is required to publish a Work Graph. Run `orgx-wizard auth login` or set ORGX_API_KEY.");
  }

  const url = buildOrgxApiUrl("/client/work-graph/reports", auth.baseUrl);
  const payload = buildWorkGraphReportPostPayload(report, options);
  const payloadBytes = Buffer.byteLength(JSON.stringify(payload));
  if (payloadBytes >= WORK_GRAPH_REPORT_CHUNK_UPLOAD_THRESHOLD_BYTES) {
    return publishWorkGraphReportInChunks({
      auth,
      payload,
      url,
      ...(options.signal ? { signal: options.signal } : {}),
    });
  }

  const encoded = encodeWorkGraphReportPostBody(payload);
  const response = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${auth.apiKey}`,
      ...encoded.headers,
    },
    body: encoded.body,
    signal: options.signal ?? AbortSignal.timeout(15_000),
  });

  return {
    ok: response.ok,
    status: response.status,
    url,
    data: await parseResponse(response),
  };
}

export async function publishWorkGraphEvents(
  fingerprint: string,
  patch: Record<string, unknown>,
  options: PublishWorkGraphEventsOptions = {},
): Promise<PublishWorkGraphReportResult> {
  const auth = await resolveOrgxAuth();
  if (!auth) {
    throw new Error("OrgX auth is required to replay Work Graph hook events. Run `orgx-wizard auth login` or set ORGX_API_KEY.");
  }

  const url = buildOrgxApiUrl("/client/work-graph/events", auth.baseUrl);
  const response = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${auth.apiKey}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      work_graph_fingerprint: fingerprint,
      patch,
    }),
    signal: options.signal ?? AbortSignal.timeout(15_000),
  });

  return {
    ok: response.ok,
    status: response.status,
    url,
    data: await parseResponse(response),
  };
}
